import {toast} from '@wandb/components';
import {type ExecutionResult} from 'graphql';
import {useCallback} from 'react';
import {useParams} from 'react-router';

import {
  type CreateNotebookMutation,
  type CreateSandboxInput,
  type CreateSandboxMutation,
  type DuplicateNotebookMutation,
  NotebookTemplate,
  SandboxMode,
  useCreateNotebookMutation,
  useCreateSandboxMutation,
  useDuplicateNotebookMutation,
} from '../../../../generated/graphql';
import {useOpenMarimoNotebookRuntime} from '../../../../routes/notebooks/useOpenMarimoNotebookRuntime';
import {useSelector} from '../../../../state/hooks';
import {
  recordRumAction,
  recordRumError,
} from '../../../../util/datadog/datadogRum';
import {propagateErrorsContext} from '../../../../util/errors';
import {showNotebookQuotaModalForError} from '../../../NotebookQuotaModal/notebookQuotaModalStore';
import {usePanelContext} from '../../../Panel/PanelContextProvider';
import {useStablePanelId} from '../../../Panel/PanelIDContext';
import {type MarimoConfig} from '../../../PanelMarimo/common';
import {MarimoMode, useMarimoState} from '../../../PanelMarimo/store';
import {useMarimoSettingsStateContext} from './MarimoSettingsContextProvider';

export const LOADING_STRINGS = [
  'Loading notebook...',
  'Creating sandbox...',
  'Setting up environment...',
  'Installing packages...',
  'Connecting to API...',
];

const LOADING_STRING_INTERVAL = 3000;

async function forkOrCreateNotebook(
  createOrForkMutation: () => Promise<
    ExecutionResult<CreateNotebookMutation | DuplicateNotebookMutation>
  >,
  updateConfig: (config: MarimoConfig) => void,
  onNotebookCreation: (value: string | null) => void
) {
  const errorMsg = 'Error creating notebook';
  try {
    const notebookResult = await createOrForkMutation();
    let notebookId: string | undefined;
    if (notebookResult.data && 'createNotebook' in notebookResult.data) {
      notebookId = notebookResult.data?.createNotebook?.notebook?.id;
    } else if (
      notebookResult.data &&
      'duplicateNotebook' in notebookResult.data
    ) {
      notebookId = notebookResult.data?.duplicateNotebook?.notebook?.id;
    }

    if ((notebookResult.errors?.length ?? 0) > 0) {
      handleError(notebookResult.errors![0]!, errorMsg);
      return undefined;
    }

    if (notebookId) {
      updateConfig({
        notebookId,
      });
      onNotebookCreation(notebookId);
      return notebookId;
    } else {
      handleError(new Error('No notebook ID returned'), errorMsg);
      return undefined;
    }
  } catch (error) {
    handleError(error as Error, errorMsg);
    return undefined;
  }
}

function handleError(error: Error, errorMsg: string) {
  // Out of storage or sandbox credit: the quota modal is the answer, not a
  // failure toast. The sandbox message marks a launch; the rest are creates.
  const action = errorMsg.includes('sandbox') ? 'launch' : 'create';
  if (showNotebookQuotaModalForError(error, action)) {
    return;
  }
  toast.error(errorMsg);
  recordRumError(new Error(errorMsg), {
    context: {
      message: error.message,
    },
  });
}

async function createSandbox(
  createSandboxMutation: ({
    variables,
  }: {
    variables: CreateSandboxInput;
  }) => Promise<ExecutionResult<CreateSandboxMutation>>,
  notebookId: string,
  entityName: string,
  projectName: string,
  runNames: string[],
  mode: SandboxMode,
  workspaceID: string | undefined
): Promise<{url: string; sandboxId: string; token: string} | undefined> {
  const errorMsg = 'Error creating sandbox';
  try {
    const sandboxResult = await createSandboxMutation({
      variables: {
        notebookId,
        mode,
        entityName,
        projectName,
        runNames,
        workspaceID,
      },
    });
    if ((sandboxResult.errors?.length ?? 0) > 0) {
      handleError(sandboxResult.errors![0]!, errorMsg);
      return undefined;
    }
    const url = sandboxResult.data?.createSandbox?.url;
    const sandboxId = sandboxResult.data?.createSandbox?.id;
    const token = sandboxResult.data?.createSandbox?.token;
    if (!url) {
      handleError(new Error('No sandbox URL returned'), errorMsg);
      return undefined;
    }
    if (!sandboxId) {
      handleError(new Error('No sandbox ID returned'), errorMsg);
      return undefined;
    }
    if (!token) {
      handleError(new Error('No sandbox token returned'), errorMsg);
      return undefined;
    }
    return {url, sandboxId, token};
  } catch (error) {
    handleError(error as Error, errorMsg);
    return undefined;
  }
}

function startLoading(
  setIsLoading: (isLoading: boolean) => void,
  setLoadingString: (loadingString: string) => void
) {
  setIsLoading(true);
  let i = 0;
  const id = window.setInterval(() => {
    i = (i + 1) % LOADING_STRINGS.length;
    setLoadingString(LOADING_STRINGS[i]);
  }, LOADING_STRING_INTERVAL);
  return () => window.clearInterval(id);
}

function useForkOrCreateNotebookClick(
  createOrForkMutation: () => Promise<
    ExecutionResult<CreateNotebookMutation | DuplicateNotebookMutation>
  >,
  setIsLoading: (isLoading: boolean) => void,
  setLoadingString: (loadingString: string) => void,
  onNotebookCreation: (value: string | null) => void
) {
  const {updateConfig, panelId} = useMarimoSettingsStateContext();
  const setPanelState = useMarimoState(state => state.setPanelState);
  return useCallback(async () => {
    setIsLoading(true);
    setLoadingString('Creating notebook...');
    try {
      const notebookId = await forkOrCreateNotebook(
        createOrForkMutation,
        updateConfig,
        onNotebookCreation
      );
      if (notebookId != null) {
        setPanelState(panelId, {
          mode: MarimoMode.STATIC,
          url: undefined,
          sandboxId: undefined,
          token: undefined,
        });
      }
      return notebookId;
    } finally {
      setIsLoading(false);
    }
  }, [
    createOrForkMutation,
    updateConfig,
    panelId,
    setPanelState,
    setIsLoading,
    setLoadingString,
    onNotebookCreation,
  ]);
}

export const useOnCreateNotebookClick = (
  setIsLoading: (isLoading: boolean) => void,
  setLoadingString: (loadingString: string) => void,
  onNotebookCreation: (value: string | null) => void
) => {
  const {projectName, entityName} = useParams<{
    projectName: string;
    entityName: string;
  }>();
  const [createNotebookMutation] = useCreateNotebookMutation({
    variables: {template: NotebookTemplate.Basic, entityName, projectName},
    context: propagateErrorsContext(),
  });
  return useForkOrCreateNotebookClick(
    createNotebookMutation,
    setIsLoading,
    setLoadingString,
    onNotebookCreation
  );
};

export function useOnForkNotebookClick(
  selectedOptionID: string | null,
  setIsLoading: (isLoading: boolean) => void,
  setLoadingString: (loadingString: string) => void,
  onNotebookFork: (value: string | null) => void
) {
  const [duplicateNotebookMutation] = useDuplicateNotebookMutation({
    variables: {id: selectedOptionID ?? ''},
    context: propagateErrorsContext(),
  });
  return useForkOrCreateNotebookClick(
    duplicateNotebookMutation,
    setIsLoading,
    setLoadingString,
    onNotebookFork
  );
}

function useCreateSandboxAction(
  notebookId: string,
  mode: SandboxMode,
  onUrl: (url: string, sandboxId: string, token: string) => void,
  setIsLoading: (b: boolean) => void,
  setLoadingString: (s: string) => void,
  runNames: string[],
  workspaceID: string | undefined
) {
  const [createSandboxMutation] = useCreateSandboxMutation({
    context: {...propagateErrorsContext(), doNotRetry: true},
  });
  const {projectName, entityName} = useParams<{
    projectName: string;
    entityName: string;
  }>();
  return useCallback(async () => {
    const stopLoading = startLoading(setIsLoading, setLoadingString);
    try {
      const sandboxResult = await createSandbox(
        createSandboxMutation,
        notebookId,
        entityName,
        projectName,
        runNames,
        mode,
        workspaceID
      );
      if (sandboxResult == null) {
        return;
      }
      const {url, sandboxId, token} = sandboxResult;
      onUrl(url, sandboxId, token);
    } finally {
      stopLoading();
      setIsLoading(false);
    }
  }, [
    createSandboxMutation,
    notebookId,
    entityName,
    projectName,
    setIsLoading,
    setLoadingString,
    onUrl,
    mode,
    runNames,
    workspaceID,
  ]);
}

function useOnClickRunCodeButton(
  notebookId: string,
  panelId: string,
  viewID: string,
  setIsLoading: (isLoading: boolean) => void,
  setLoadingString: (loadingString: string) => void
) {
  const workspaceID = useSelector(state => state.views.views[viewID]?.id);
  const setPanelState = useMarimoState(state => state.setPanelState);
  const runNames = useMarimoState(
    state => state.getPanelState(panelId).runNames
  );
  const onUrl = useCallback(
    (url: string, sandboxId: string, token: string) => {
      setPanelState(panelId, {
        mode: MarimoMode.RUN,
        url,
        sandboxId,
        token,
      });
      recordRumAction('panel_marimo_notebook_run_sandbox_created', {
        notebookId,
        panelId,
        sandboxId,
      });
    },
    [setPanelState, panelId, notebookId]
  );
  return useCreateSandboxAction(
    notebookId,
    SandboxMode.Run,
    onUrl,
    setIsLoading,
    setLoadingString,
    runNames,
    workspaceID
  );
}

export function useOnClickRunCodeButtonInSettings(
  notebookId: string,
  setIsLoading: (isLoading: boolean) => void,
  setLoadingString: (loadingString: string) => void
) {
  const {panelId, viewID} = useMarimoSettingsStateContext();
  return useOnClickRunCodeButton(
    notebookId,
    panelId,
    viewID,
    setIsLoading,
    setLoadingString
  );
}

export function useOnClickRunCodeButtonInPanel(
  notebookId: string,
  setIsLoading: (isLoading: boolean) => void,
  setLoadingString: (loadingString: string) => void
) {
  const panelId = useStablePanelId();
  const {panelRef} = usePanelContext();
  return useOnClickRunCodeButton(
    notebookId,
    panelId,
    panelRef.viewID,
    setIsLoading,
    setLoadingString
  );
}

function useOnClickViewNotebookButton(notebookId: string, canEdit: boolean) {
  const openNotebookRuntime = useOpenMarimoNotebookRuntime();
  return useCallback(
    () =>
      openNotebookRuntime(notebookId, undefined, {
        newTab: true,
        readOnly: !canEdit,
      }),
    [notebookId, canEdit, openNotebookRuntime]
  );
}

export function useOnClickViewNotebookButtonInSettings(
  notebookId: string,
  canEdit: boolean
) {
  return useOnClickViewNotebookButton(notebookId, canEdit);
}
